import { Box, Grid, Skeleton, Text } from '@chakra-ui/react';

import { FormatType, getFormatter } from '@revelio/replots';

import type { CompanyPostings } from '../hooks/usePostingsComparison';

const intFormatter = getFormatter(FormatType.INTEGER);

interface CompanyComparePostingsProps {
  postings: CompanyPostings[];
  companyOrder: number[];
  isLoading: boolean;
}

interface PostingRowProps {
  label: string;
  values: Array<{ text: string; color?: string }>;
  colCount: number;
}

const PostingRow = ({ label, values, colCount }: PostingRowProps) => (
  <Grid
    templateColumns={`140px repeat(${colCount}, 1fr)`}
    gap={2}
    py={1.5}
    borderBottom="1px solid"
    borderColor="gray.50"
  >
    <Text fontSize="xs" color="gray.500" alignSelf="center">
      {label}
    </Text>
    {values.map((v, i) => (
      <Box key={i} textAlign="center">
        <Text fontSize="sm" color={v.color ?? 'gray.700'}>
          {v.text}
        </Text>
      </Box>
    ))}
  </Grid>
);

export const CompanyComparePostings = ({
  postings,
  companyOrder,
  isLoading,
}: CompanyComparePostingsProps) => {
  const postingsMap = new Map(postings.map((p) => [p.companyId, p]));
  const colCount = companyOrder.length;

  if (isLoading) {
    return <Skeleton height="120px" borderRadius="md" />;
  }

  if (postings.length === 0) {
    return null;
  }

  return (
    <Box>
      <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={2}>
        Job Postings
      </Text>

      <PostingRow
        label="Active Postings"
        values={companyOrder.map((rcid) => {
          const p = postingsMap.get(rcid);
          const v = p?.activePostings;
          return {
            text: v !== null && v !== undefined ? intFormatter(v) : '\u2014',
            color:
              v !== null && v !== undefined && v > 0 ? 'blue.600' : undefined,
          };
        })}
        colCount={colCount}
      />

      <PostingRow
        label="Expected Hires"
        values={companyOrder.map((rcid) => {
          const p = postingsMap.get(rcid);
          const v = p?.expectedHires;
          return {
            text: v !== null && v !== undefined ? intFormatter(v) : '\u2014',
          };
        })}
        colCount={colCount}
      />

      <PostingRow
        label="Time to Fill"
        values={companyOrder.map((rcid) => {
          const p = postingsMap.get(rcid);
          const v = p?.timeToFill;
          return {
            text:
              v !== null && v !== undefined
                ? `${Math.round(v)} days`
                : '\u2014',
          };
        })}
        colCount={colCount}
      />
    </Box>
  );
};
